import { Injectable, OnDestroy } from '@angular/core'
import orderBy from 'lodash-es/orderBy'
import { combineLatest, of } from 'rxjs'
import { Observable } from 'rxjs'
import { map, switchMap, tap } from 'rxjs/operators'
import { SubSink } from 'subsink'
import { ApiService } from './api.service'
import { FirestoreService2 } from './firestore.service2'
import { GlobalService } from './global.service'
import { coerceToDate } from './timestampUtils'
import { use } from './utils'

@Injectable({ providedIn: 'root' })

export class RankingService implements OnDestroy {

  private subs = new SubSink()
  private fss: FirestoreService2

  constructor(
    public g: GlobalService,
  ) { console.info(`## ${this.constructor.name}`) }

  ngOnDestroy() {
    this.subs.unsubscribe()
  }

  getRanking(view = null, challenge = null) {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    return new Promise(async (resolve, reject) => {
      if (view === 'segment') {
        this.subs.add(
          this.fss.list('segments', { where: [['active', '==', true]], api: true }).subscribe(segments => {
            if (challenge) {
              segments.map(e => e.ranking && e.ranking.filter(o => o.challenge === challenge ? e[challenge] = o.credits : null))
              segments = segments.sort((a, b) => a[challenge] === b[challenge] ? 0 : a[challenge] < b[challenge] ? -1 : 1).reverse().map((row, i) => ({ ...row, rank: i + 1 }))
              const ranking = segments.filter(o => o.id === this.g.user.segment.id)
              if (ranking && ranking[0]) {
                this.g.user[challenge] = { rank: ranking[0].rank, credits: ranking[0].credits }
                resolve({ rank: ranking[0].rank, credits: ranking[0].credits })
              }
            } else {
              segments.map(e => {
                e.credits = 0
                e.ranking && e.ranking.filter(o => e.credits += o.credits)
              })
              segments = segments.sort((a, b) => a['credits'] === b['credits'] ? 0 : a['credits'] < b['credits'] ? -1 : 1).reverse().map((row, i) => ({ ...row, rank: i + 1 }))
              const ranking = segments.filter(o => o.id === this.g.user.segment.id)
              if (ranking && ranking[0]) {
                this.g.user.rankingSegment = { rank: ranking[0].rank, credits: ranking[0].credits }
                resolve({ rank: ranking[0].rank, credits: ranking[0].credits })
              }
            }
          })
        )
      } else {
        // TODO PF pass the ranking to functions using action
        const api: ApiService = use<ApiService>(ApiService)
        let users = await api.post(`db/action`, { action: 'list', nick: this.g.nick, collection: 'users', api: true }, true).toPromise()
        users = users.sort((a, b) => a['credits'] === b['credits'] ? 0 : a['credits'] < b['credits'] ? -1 : 1).reverse().map((row, i) => ({ ...row, rank: i + 1 }))
        const ranking = users
        if (ranking && ranking[0]) {
          this.g.user.ranking = { rank: ranking[0].rank, credits: ranking[0].credits }
          resolve({ rank: ranking[0].rank, credits: ranking[0].credits })
        }
      }
    })

  }

  /**
   * Normalizes a Unity result `createdAt` to Unix ms.
   * `coerceToDate` passes strings/numbers through as-is, so fall back to
   * `new Date(value)` when it does not return a Date. Returns 0 when the
   * value cannot be interpreted.
   */
  private unityCreatedAtMs(value: any): number {
    const d = coerceToDate(value)
    const date = d instanceof Date && !isNaN(d.getTime()) ? d : new Date(value)
    return isNaN(date.getTime()) ? 0 : date.getTime()
  }

  /**
   * Creates a ranking of best scores per user for a Unity game
   * Each user appears only once, with their highest `points` entry
   * from the `challenges` collection. Ties keep the oldest entry
   * (the user who reached the score first ranks ahead).
   * @param {any} ranking
   * @returns {Observable<any[]>}
   */
  private getUnityBestScores(ranking: any): Observable<any[]> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    return this.fss.list('challenges', { api: true, where: [['challengeId', '==', ranking.challengeId]] }).pipe(
      map((results: any[]) => {
        const sorted = orderBy(results.filter(r => r.active !== false && r.email), [r => this.unityCreatedAtMs(r.createdAt)], ['asc'])
        const userMap: any = {}
        for (const r of sorted) {
          const points = Number(r.points) || 0
          if (!userMap.hasOwnProperty(r.email) || points > userMap[r.email].credits) {
            userMap[r.email] = {
              email: r.email,
              displayName: r.displayName || r.email,
              credits: points,
              createdAtMs: this.unityCreatedAtMs(r.createdAt),
              segment: r.segment || null,
            }
          }
        }
        return Object.values(userMap)
      })
    )
  }

  /**
   * Creates a ranking by segment
   * If challengeId is provided, makes a ranking based on a specific challenge
   * If challengeId is not provided, makes the general ranking
   * @param {string} challengeId
   * @returns {Observable<any[]>}
   */
  getSegmentRanking(ranking: any = null, mode = 'absolute'): Observable<any[]> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    if (!ranking || ranking.type === 'general') {
      return this.fss.list('segments', { api: true, where: [['active', '==', true], ['hasRank', '==', true]] }).pipe(
        map((segments: any[]) => segments.map(s => ({ ...s, credits: s.credits || 0, debits: s.debtis || 0, balance: s.balance || 0 }))),
        map((segments: any[]) => orderBy(segments, ['credits'], ['desc']).map((s, index) => ({ ...s, index: index + 1, rank: index + 1 }))),
        tap((segments: any[]) => this.g.user.rankingSegment = {
          rank: (segments.find(s => s.id === this.g.user.segment.id) || { index: null }).index,
          credits: (segments.find(s => s.id === this.g.user.segment.id) || { credits: 0 }).credits
        }),
      )
    } else if (ranking.type === 'unity') {
      const api: ApiService = use<ApiService>(ApiService)
      return combineLatest([
        this.getUnityBestScores(ranking),
        api.post('db/action', { action: 'list', nick: this.g.nick, collection: 'users', api: true, where: [['active', '==', true]] }, true),
        this.fss.list('segments', { api: true, where: [['active', '==', true], ['hasRank', '==', true]] })
      ]).pipe(
        map(([bestScores, users, segments]: any[]) => {
          const segmentMap: any = {}
          for (const score of bestScores) {
            const user = users.find(u => u.email === score.email) || score
            const segmentId = (user.segment && user.segment.id) || (score.segment && score.segment.id)
            if (!segmentId || !segments.find(s => s.id === segmentId)) continue
            if (!segmentMap[segmentId]) {
              segmentMap[segmentId] = {
                id: segmentId,
                name: segments.find(s => s.id === segmentId).name,
                credits: 0,
                users: 0
              }
            }
            segmentMap[segmentId].credits += score.credits
            if (users.find(u => u.email === score.email)) segmentMap[segmentId].users++
          }
          return Object.values(segmentMap).map((seg: any) => mode === 'averange'
            ? { ...seg, _credits: seg.credits, credits: seg.users ? parseFloat((seg.credits / seg.users).toFixed(2)) : seg.credits }
            : seg)
        }),
        map(ranks => orderBy(ranks, ['credits'], ['desc']).map((s, index) => ({ ...s, index: index + 1 })))
      )
    } else {
      // TODO PF pass ranking to functions
      const api: ApiService = use<ApiService>(ApiService)
      return api.post('db/action', { action: 'list', nick: this.g.nick, collection: 'transactions', api: true, where: [['active', '==', true], ['type', '==', 'segments'], ['challengeId', '==', ranking.challengeId]] }, true)
        .pipe(
          map((trans: any[]) => trans.filter(t => typeof t.segment === 'object')),
          switchMap((trans: any) => this.fss.list('segments', { api: true, where: [['active', '==', true], ['hasRank', '==', true]] })
            .pipe(map((segments: any[]) => trans.filter(tr => !!segments.find(s => s.id === tr.segment.id)).map(tr => ({ ...tr, segment: segments.find(s => s.id === tr.segment.id) })))))
        )
        .pipe(
          map((trans: any[]) => {
            const segmentMap: any = {}
            for (const tr of trans) {
              if (segmentMap.hasOwnProperty(tr.segment.id)) segmentMap[tr.segment.id] += tr.orderPoints
              else segmentMap[tr.segment.id] = tr.orderPoints
            }
            const array = Object.keys(segmentMap).map(segID => ({
              id: segID,
              name: trans.find(t => t.segment.id === segID).segment.name,
              credits: segmentMap[segID]
            }))
            return array
          }),
          switchMap((ranks: any[]) => {
            if (mode === 'averange') {
              const api: ApiService = use<ApiService>(ApiService)
              return api.post('db/action', { action: 'list', nick: this.g.nick, collection: 'users', api: true, where: [['active', '==', true]] }, true)
                .pipe(
                  map((users: any[]) => ranks.map(r => ({
                    ...r,
                    _credits: r.credits,
                    credits: parseFloat((r.credits / users.filter(u => u.segment.id === r.id).length).toFixed(2)),
                    users: users.filter(u => u.segment.id === r.id).length
                  }))),
                )
            }
            return of(ranks)
          }),
          map(ranks => orderBy(ranks, ['credits'], ['desc']).map((s, index) => ({ ...s, index: index + 1 }))),
          // tap(console.log)
        )
    }
  }

  /**
   * Creates a ranking by user
   * If showAll is true, creates a ranking with ALL users
   * If showAll is false, creates a ranking only with users that have credits
   * @param {boolean} showAll
   * @returns {Observable<any[]>}
   */
  getUserRanking(ranking: any = null, showAll = false): Observable<any[]> {
    if (!ranking || ranking.type === 'general') {
      const api: ApiService = use<ApiService>(ApiService)
      const obs = showAll ? api.post('db/action', { action: 'list', nick: this.g.nick, collection: 'users', api: true }, true) : api.post('db/action', { action: 'list', nick: this.g.nick, collection: 'users', api: true, where: [['credits', '>', 0]] }, true)
      return obs.pipe(
        map((users: any[]) => users.map(s => ({ ...s, credits: s.credits || 0 }))),
        map((users: any[]) => orderBy(users, ['credits'], ['desc'])),
        map((users: any[]) => users.map((s, index) => ({
          displayName: s.displayName || s.firstName || s.name,
          uid: s.uid || s.id,
          email: s.email,
          credits: s.credits,
          index: index + 1,
          rank: index + 1
        })))
      )
    } else if (ranking.type === 'challenge') {
      // TODO PF pass to functions
      const api: ApiService = use<ApiService>(ApiService)
      return api.post('db/action', { action: 'list', nick: this.g.nick, collection: 'transactions', api: true, where: [['active', '==', true], ['challengeId', '==', ranking.challengeId]] }, true).pipe(
        map((trans: any[]) => {
          trans = trans.filter(o => !o.type || o.type !== 'segments')
          if (ranking.rankingByGroup) trans = trans.filter(o => o.group === ranking.group)
          const userMap: any = {}
          for (const tr of trans) {
            if (userMap.hasOwnProperty(tr.email)) userMap[tr.email].value += tr.orderPoints
            else userMap[tr.email] = { name: tr.displayName, value: tr.orderPoints }
          }
          const array = Object.values(userMap).map(user => ({ displayName: user['name'], credits: user['value'] }))
          // console.log(array)
          return orderBy(array, ['credits'], ['desc']).map((s, index) => ({ ...s, index: index + 1 }))
        })
      )
    } else if (ranking.type === 'unity') {
      return this.getUnityBestScores(ranking).pipe(
        map((scores: any[]) => orderBy(scores, [s => s.credits, s => s.createdAtMs], ['desc', 'asc'])),
        map((scores: any[]) => scores.map((s, index) => ({
          displayName: s.displayName,
          email: s.email,
          credits: s.credits,
          index: index + 1,
          rank: index + 1
        })))
      )
    } else {
      const api: ApiService = use<ApiService>(ApiService)
      // TODO PF pass to functions
      return api.post('db/action', { action: 'list', nick: this.g.nick, collection: 'transactions', api: true, where: [['active', '==', true], ['tag', '==', ranking.tag]] }, true).pipe(
        map((trans: any[]) => {
          trans = trans.filter(o => !o.type || o.type !== 'segments')
          if (ranking.rankingByGroup) trans = trans.filter(o => o.group === ranking.group)
          const userMap: any = {}
          for (const tr of trans) {
            if (userMap.hasOwnProperty(tr.email)) userMap[tr.email].value += tr.orderPoints
            else userMap[tr.email] = { name: tr.displayName, value: tr.orderPoints }
          }
          const array = Object.values(userMap).map(user => ({ displayName: user['name'], credits: user['value'] }))
          // console.log(array)
          return orderBy(array, ['credits'], ['desc']).map((s, index) => ({ ...s, index: index + 1 }))
        })
      )
    }
  }

  selectRankMethod(ranking: any): Observable<any> {
    if (ranking.view === 'segment') {
      return this.getSegmentRanking(ranking, ranking.mode || 'absolute')
    } else {
      return this.getUserRanking(ranking)
    }
  }

  generateRankEmailRows(ranksConfig: any[], htmlBase: string): Observable<any> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    if (!ranksConfig || !ranksConfig.length) return of(htmlBase)
    htmlBase = htmlBase.replace(/\n/g, '').replace(/(\[\[RANK=(.+?)\]\])(.+?)(\[\[\/RANK\]\])/gm, '$1_ROWS_$4')
    const matches = htmlBase.match(/(\[\[RANK=(.+?)\]\])(.+?)(\[\[\/RANK\]\])/gm)
    const observables = []

    for (const mat of matches) {
      const id = mat.replace(/(\[\[RANK=(.+?)\]\])(.+?)(\[\[\/RANK\]\])/gm, '$2')
      const config = ranksConfig.find(c => c.challenge === id)
      observables.push(
        this.fss.get('rankings', id, { api: true })
          .pipe(switchMap((rankConfig: any) => this.selectRankMethod(rankConfig).pipe(map((ranks: any[]) => {
            const rows = []
            for (const r of ranks.splice(0, config.qty).map((r, i) => ({ ...r, i: i + 1 }))) {
              rows.push(config.row.replace('[[POINTS]]', r.credits).replace('[[EQUIPA]]', r.name || r.displayName || r.name).replace('[[NO]]', r.i))
            }
            return { mat, rows: rows.join('') }
          }))))
      )
    }

    return combineLatest(observables).pipe(map((rows: any[]) => {
      let html = htmlBase
      for (const r of rows) html = html.replace(r.mat, r.rows)
      return html
    }))
  }
}
